import { useEffect, useRef, useState } from "react";
import { FindWrongNoteActivity, NotationPickActivity, PitchMatchActivity, SingPhraseActivity, SameDiffActivity, QuickPickActivity, MatchCardsActivity, PracticeShell, MatchMyPitchActivity, SingIntervalActivity } from "../components/LearnActivities";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Music, Volume2, Check, X, ArrowLeft, ArrowRight, BookOpen, ListChecks, Landmark, Hand } from "lucide-react";
import { childApi } from "../lib/api";
import { playAudioSpec, installAudioUnlock } from "../lib/audio";
import { StaveView } from "../components/Stave";
import { DEFAULT_A4 } from "../lib/tuning";

function AudioButton({ audio, idx, label = "Listen" }) {
  const [busy, setBusy] = useState(false);
  const [played, setPlayed] = useState(false);
  const [err, setErr] = useState("");
  const hear = async () => {
    setBusy(true);
    setErr("");
    try {
      await playAudioSpec(audio);
      setPlayed(true);
    } catch (e) {
      console.error("[ChoirQuest audio] learn playback failed:", e);
      setErr("We couldn't play the sound. Check your volume and silent switch, then try again.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid={`learn-audio-${idx}`}>
      <button onClick={hear} disabled={busy} data-testid={`learn-audio-${idx}-play`}
        className="inline-flex items-center gap-2 px-5 py-3.5 rounded-2xl bg-[#C69B3D] text-[#0B192C] font-bold hover:bg-[#D8AB49] transition-colors min-h-[52px] disabled:opacity-50">
        <Volume2 className="w-5 h-5" aria-hidden /> {played ? "↻ Play again" : `▶ ${label}`}
      </button>
      {err && <p role="alert" className="mt-2 text-sm text-[#F2B8C6]" data-testid={`learn-audio-${idx}-error`}>{err}</p>}
    </div>
  );
}

function CheckQuestion({ q, idx, hint }) {
  const [picked, setPicked] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const answered = picked !== null;
  return (
    <div className="rounded-2xl border-2 border-[#E5DEC9] bg-white p-5" data-testid={`learn-check-${idx}`}>
      <p className="text-xs font-bold uppercase tracking-widest text-[#C69B3D]">
        {hint ? "Try it" : `Quick check ${idx + 1}`}
      </p>
      {q.audio && (
        <div className="mt-3 rounded-xl bg-[#0B192C] p-4">
          <AudioButton audio={q.audio} idx={`check-${idx}`} />
        </div>
      )}
      <p className="font-display text-lg font-bold text-[#0B192C] mt-2 whitespace-pre-line">{q.text}</p>
      {q.notation && (
        <div className="mt-3">
          <StaveView notation={q.notation} width={Math.min(520, window.innerWidth - 96)} testid={`learn-check-stave-${idx}`} />
        </div>
      )}
      {hint && !answered && (
        <button onClick={() => setShowHint(!showHint)} data-testid={`learn-check-${idx}-hint`}
          className="mt-2 text-sm font-semibold text-[#8a6a10] underline min-h-[44px]">
          {showHint ? "Hide hint" : "Need a hint?"}
        </button>
      )}
      {hint && showHint && !answered && (
        <p className="mt-1 text-sm text-[#566270] bg-[#F6EED8] rounded-xl p-3" data-testid={`learn-check-${idx}-hint-text`}>{hint}</p>
      )}
      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        {q.options.map((opt, i) => {
          let cls = "border-[#E5DEC9] bg-[#FAF7EE] text-[#0B192C] hover:border-[#C69B3D]";
          if (answered && i === q.correct) cls = "border-[#2D6A4F] bg-[#EBF6F0] text-[#2D6A4F]";
          else if (answered && i === picked) cls = "border-[#A62B2B] bg-[#FBF0F2] text-[#A62B2B]";
          return (
            <button key={opt} onClick={() => !answered && setPicked(i)} disabled={answered}
              data-testid={`learn-check-${idx}-option-${i}`}
              className={`text-left px-4 py-3 rounded-xl border-2 font-semibold text-sm transition-colors min-h-[48px] flex items-center gap-2 ${cls}`}>
              {answered && i === q.correct && <Check className="w-4 h-4 shrink-0" aria-hidden />}
              {answered && i === picked && i !== q.correct && <X className="w-4 h-4 shrink-0" aria-hidden />}
              {opt}
            </button>
          );
        })}
      </div>
      {answered && (
        <p className={`mt-3 text-sm font-semibold ${picked === q.correct ? "text-[#2D6A4F]" : "text-[#A62B2B]"}`}
          data-testid={`learn-check-${idx}-verdict`}>
          {picked === q.correct ? "Correct — well done!" : "Nearly!"}{q.explanation ? ` ${q.explanation}` : ""}
        </p>
      )}
    </div>
  );
}

function TapEntry({ s, idx }) {
  const [playing, setPlaying] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const t0 = useRef(0);
  const target = s.target_seconds;
  const tol = s.tolerance ?? 0.8;
  const play = async () => {
    setErr("");
    setResult(null);
    setPlaying(true);
    t0.current = performance.now();
    try {
      await playAudioSpec(s.audio);
    } catch (e) {
      console.error("[ChoirQuest audio] tap-entry playback failed:", e);
      setErr("We couldn't play the sound. Check your volume and silent switch, then try again.");
      setPlaying(false);
      return;
    }
    setTimeout(() => setPlaying(false), (target + tol + 1.5) * 1000);
  };
  const tap = () => {
    if (!playing || result) return;
    const diff = (performance.now() - t0.current) / 1000 - target;
    const tone = Math.abs(diff) <= tol ? "good" : diff < 0 ? "early" : "late";
    setResult({ tone, msg: s[`feedback_${tone}`] });
  };
  return (
    <div className="rounded-2xl border-2 border-[#C69B3D]/50 bg-white p-5 space-y-4" data-testid={`tap-entry-${idx}`}>
      <p className="text-base text-[#1C242B] leading-relaxed">{s.instruction}</p>
      <div className="flex gap-3 flex-wrap">
        <button onClick={play} data-testid={`tap-entry-${idx}-play`}
          className="inline-flex items-center gap-2 px-5 py-3.5 rounded-2xl bg-[#0B192C] text-[#FAF7EE] font-bold hover:bg-[#12253E] transition-colors min-h-[52px]">
          <Volume2 className="w-5 h-5" aria-hidden /> {result ? "↻ Try again" : "▶ Play"}
        </button>
        <button onClick={tap} disabled={!playing || !!result} data-testid={`tap-entry-${idx}-tap`}
          className={`flex-1 px-6 py-4 rounded-2xl font-bold text-lg min-h-[60px] transition-colors ${
            playing && !result ? "bg-[#C69B3D] text-[#0B192C] hover:bg-[#D8AB49]" : "bg-[#F3EFE0] text-[#566270]/50 cursor-not-allowed"}`}>
          <Hand className="w-5 h-5 inline mr-2" aria-hidden />TAP!
        </button>
      </div>
      {result && (
        <p className={`text-sm font-bold ${result.tone === "good" ? "text-[#2D6A4F]" : "text-[#A62B2B]"}`}
          data-testid={`tap-entry-${idx}-verdict`}>{result.msg}</p>
      )}
      {err && <p role="alert" className="text-sm text-[#A62B2B]" data-testid={`tap-entry-${idx}-error`}>{err}</p>}
    </div>
  );
}

function BreathPoints({ s, idx }) {
  const [marks, setMarks] = useState(new Set());
  const [checked, setChecked] = useState(false);
  const gaps = s.gaps || {};
  const good = Object.keys(gaps).filter((k) => gaps[k].v === "good").map(Number);
  const avoid = Object.keys(gaps).filter((k) => gaps[k].v === "avoid").map(Number);
  const toggle = (i) => {
    if (checked) return;
    setMarks((m) => { const n = new Set(m); if (n.has(i)) { n.delete(i); } else { n.add(i); } return n; });
  };
  const correct = checked && marks.size > 0 && good.every((i) => marks.has(i))
    && [...marks].every((i) => !avoid.includes(i) && gaps[i]);
  return (
    <div className="rounded-2xl border-2 border-[#C69B3D]/50 bg-white p-5 space-y-4" data-testid={`breath-points-${idx}`}>
      <p className="text-base text-[#1C242B] leading-relaxed">{s.instruction}</p>
      <div className="flex flex-wrap items-center gap-y-3 text-lg font-display font-bold text-[#0B192C] leading-loose">
        {s.words.map((w, i) => (
          <span key={`${w}-${i}`} className="flex items-center">
            {i > 0 && (
              <button onClick={() => toggle(i)} data-testid={`breath-gap-${idx}-${i}`} aria-label={`breath gap before ${w}`}
                className={`mx-1 w-8 h-11 rounded-lg border-2 text-base font-bold transition-colors ${
                  marks.has(i) ? "border-[#7A1C2E] bg-[#FBF0F2] text-[#7A1C2E]" : "border-dashed border-[#E5DEC9] text-[#C9C2B2] hover:border-[#C69B3D]"}`}>
                {marks.has(i) ? "′" : "·"}
              </button>
            )}
            <span>{w}</span>
          </span>
        ))}
      </div>
      {!checked ? (
        <button onClick={() => setChecked(true)} disabled={marks.size === 0} data-testid={`breath-check-${idx}`}
          className="px-6 py-3.5 rounded-2xl bg-[#0B192C] text-[#FAF7EE] font-bold hover:bg-[#12253E] transition-colors min-h-[52px] disabled:opacity-40">
          Check my choices
        </button>
      ) : (
        <div className="space-y-2" data-testid={`breath-verdict-${idx}`}>
          <p className={`text-sm font-bold ${correct ? "text-[#2D6A4F]" : "text-[#A62B2B]"}`}>
            {correct ? "Beautiful phrasing — the line stays unbroken." : "Not quite — see the notes below, then adjust and re-check."}
          </p>
          {good.filter((i) => !marks.has(i)).map((i) => (
            <p key={i} className="text-xs text-[#566270]">Missed: {gaps[i].why}</p>
          ))}
          {[...marks].filter((i) => avoid.includes(i)).map((i) => (
            <p key={i} className="text-xs text-[#A62B2B]">Avoid: {gaps[i].why}</p>
          ))}
          {[...marks].filter((i) => !gaps[i]).map((i) => (
            <p key={i} className="text-xs text-[#A62B2B]">Extra breaths mid-thought chop the phrase up — fewer, better-placed breaths win.</p>
          ))}
          <p className="text-xs text-[#566270] italic">{s.explanation}</p>
          <button onClick={() => setChecked(false)} data-testid={`breath-retry-${idx}`}
            className="text-sm font-bold text-[#0B192C] underline min-h-[44px]">Adjust my breath marks</button>
        </div>
      )}
    </div>
  );
}

const BEAT_POS = [{ x: 50, y: 82 }, { x: 16, y: 55 }, { x: 84, y: 55 }, { x: 50, y: 14 }];

function ConductorDemo({ s, idx }) {
  const [running, setRunning] = useState(false);
  const [tapMode, setTapMode] = useState(false);
  const [result, setResult] = useState(null);
  const dotRef = useRef(null);
  const labelRef = useRef(null);
  const startRef = useRef(0);
  const beatMs = s.beat_ms || 900;

  useEffect(() => {
    if (!running) return undefined;
    startRef.current = performance.now();
    let raf;
    const tick = () => {
      const phase = ((performance.now() - startRef.current) / beatMs) % 4;
      const i = Math.floor(phase);
      const f = phase - i;
      const a = BEAT_POS[i];
      const b = BEAT_POS[(i + 1) % 4];
      if (dotRef.current) {
        dotRef.current.style.left = `${a.x + (b.x - a.x) * f}%`;
        dotRef.current.style.top = `${a.y + (b.y - a.y) * f}%`;
      }
      if (labelRef.current) {
        labelRef.current.textContent = phase >= 3 ? "Breathe in… (upbeat)" : phase < 0.4 ? "SING!" : "";
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [running, beatMs]);

  const tap = () => {
    if (!running || !tapMode || result) return;
    const phase = ((performance.now() - startRef.current) / beatMs) % 4;
    const tolBeats = (s.tolerance_ms || 380) / beatMs;
    const d = Math.min(phase, 4 - phase);
    const tone = d <= tolBeats ? "good" : phase < 2 ? "late" : "early";
    setResult({ tone, msg: s[`feedback_${tone}`] });
  };

  return (
    <div className="rounded-2xl border-2 border-[#C69B3D]/50 bg-white p-5 space-y-4" data-testid={`conductor-${idx}`}>
      <p className="text-base text-[#1C242B] leading-relaxed">{s.instruction}</p>
      <div className="relative w-52 h-52 mx-auto rounded-2xl bg-[#0B192C]" data-testid={`conductor-stage-${idx}`}>
        {running && (
          <div ref={dotRef} className="absolute w-5 h-5 -ml-2.5 -mt-2.5 rounded-full bg-[#C69B3D] shadow-lg transition-none" />
        )}
        <p ref={labelRef} className="absolute bottom-2 inset-x-0 text-center text-sm font-bold text-[#FAF7EE]" />
        {!running && <p className="absolute inset-0 flex items-center justify-center text-[#FAF7EE]/60 text-sm px-6 text-center">Press Start to watch the baton</p>}
      </div>
      <div className="flex gap-3 flex-wrap">
        <button onClick={() => { setRunning(!running); setResult(null); if (running) setTapMode(false); }}
          data-testid={`conductor-${idx}-toggle`}
          className="px-5 py-3.5 rounded-2xl bg-[#0B192C] text-[#FAF7EE] font-bold hover:bg-[#12253E] transition-colors min-h-[52px]">
          {running ? "Stop" : "▶ Start the baton"}
        </button>
        {running && !tapMode && (
          <button onClick={() => { setTapMode(true); setResult(null); }} data-testid={`conductor-${idx}-tapmode`}
            className="px-5 py-3.5 rounded-2xl border-2 border-[#0B192C] text-[#0B192C] font-bold min-h-[52px]">
            Tap to sing
          </button>
        )}
        {running && tapMode && (
          <button onClick={tap} disabled={!!result} data-testid={`conductor-${idx}-tap`}
            className={`flex-1 px-6 py-4 rounded-2xl font-bold text-lg min-h-[60px] transition-colors ${
              result ? "bg-[#F3EFE0] text-[#566270]/50" : "bg-[#C69B3D] text-[#0B192C] hover:bg-[#D8AB49]"}`}>
            <Hand className="w-5 h-5 inline mr-2" aria-hidden />TAP on the downbeat!
          </button>
        )}
      </div>
      {result && (
        <p className={`text-sm font-bold ${result.tone === "good" ? "text-[#2D6A4F]" : "text-[#A62B2B]"}`}
          data-testid={`conductor-${idx}-verdict`}>{result.msg}</p>
      )}
    </div>
  );
}

function SingCheck({ s, idx }) {
  const [phase, setPhase] = useState("idle"); // idle -> cue -> sing -> check -> done
  const [err, setErr] = useState("");
  const gap = s.audio?.gap_secs ?? 4;
  const cueSecs = s.audio?.type === "echo_melody" ? (s.audio?.midi || []).length * 0.55 : 2;
  const play = async () => {
    setErr("");
    setPhase("cue");
    const p = playAudioSpec(s.audio);
    setTimeout(() => setPhase("sing"), cueSecs * 1000);
    setTimeout(() => setPhase("check"), (cueSecs + gap) * 1000);
    try {
      await p;
    } catch (e) {
      console.error("[ChoirQuest audio] sing-check failed:", e);
      setErr("We couldn't play the sound. Check your volume and silent switch, then try again.");
    }
    setPhase("done");
  };
  const label = { cue: "Listen to the cue…", sing: "Your turn — sing on 'la'!",
                  check: "The correct note — did yours match?", done: "Well done. Try again whenever you like." }[phase];
  return (
    <div className="rounded-2xl border-2 border-[#C69B3D]/50 bg-white p-5 space-y-4" data-testid={`sing-check-${idx}`}>
      <p className="text-base text-[#1C242B] leading-relaxed">{s.instruction}</p>
      <div className="flex gap-3 flex-wrap items-center">
        <button onClick={play} disabled={phase === "cue" || phase === "sing" || phase === "check"} data-testid={`sing-check-${idx}-play`}
          className="inline-flex items-center gap-2 px-5 py-3.5 rounded-2xl bg-[#0B192C] text-[#FAF7EE] font-bold hover:bg-[#12253E] transition-colors min-h-[52px] disabled:opacity-50">
          <Volume2 className="w-5 h-5" aria-hidden /> {phase === "idle" ? "▶ Start" : phase === "done" ? "↻ Try again" : "Playing…"}
        </button>
        {phase !== "idle" && <p className="text-sm font-bold text-[#7A1C2E]" data-testid={`sing-check-${idx}-phase`}>{label}</p>}
      </div>
      {err && <p role="alert" className="text-sm text-[#A62B2B]" data-testid={`sing-check-${idx}-error`}>{err}</p>}
    </div>
  );
}

function NoteGlyph({ type }) {
  const filled = type === "crotchet" || type === "quaver";
  return (
    <svg width="20" height="34" viewBox="0 0 22 34" aria-hidden>
      <ellipse cx="8" cy="27" rx="6" ry="4.5" transform="rotate(-18 8 27)" fill={filled ? "#0B192C" : "none"} stroke="#0B192C" strokeWidth="2" />
      {type !== "semibreve" && <line x1="13.5" y1="26" x2="13.5" y2="5" stroke="#0B192C" strokeWidth="2" />}
      {type === "quaver" && <path d="M13.5 5 Q 21 9 18 17" fill="none" stroke="#0B192C" strokeWidth="2" />}
    </svg>
  );
}

function ValuePyramid({ s, idx }) {
  const [playing, setPlaying] = useState(-1);
  const [err, setErr] = useState("");
  const play = async (i) => {
    setErr("");
    setPlaying(i);
    try {
      await playAudioSpec(s.levels[i].audio);
    } catch (e) {
      console.error("[ChoirQuest audio] pyramid failed:", e);
      setErr("We couldn't play the sound. Check your volume and silent switch, then try again.");
    }
    setPlaying(-1);
  };
  return (
    <div className="rounded-2xl border-2 border-[#C69B3D]/50 bg-white p-5" data-testid={`value-pyramid-${idx}`}>
      <p className="text-base text-[#1C242B] leading-relaxed mb-4">{s.instruction}</p>
      <div className="space-y-2">
        {s.levels.map((lv, i) => (
          <button key={lv.name} onClick={() => play(i)} disabled={playing !== -1} data-testid={`value-pyramid-${idx}-row-${lv.name}`}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 font-bold min-h-[52px] transition-colors ${playing === i ? "border-[#C69B3D] bg-[#F6EED8]" : "border-[#E5DEC9] bg-[#FAF7EE] hover:border-[#C69B3D]"}`}>
            <span className="w-8 text-right font-display shrink-0">{lv.count}×</span>
            <span className="flex gap-1 items-center overflow-hidden">{Array.from({ length: lv.count }).map((_, k) => <NoteGlyph key={k} type={lv.name} />)}</span>
            <span className="ml-auto text-xs text-[#566270] text-right">{lv.count} {lv.name}{lv.count > 1 ? "s" : ""}<br />{lv.beats} beat{lv.beats !== 1 ? "s" : ""} each</span>
          </button>
        ))}
      </div>
      <p className="mt-4 text-center font-display font-bold text-[#0B192C]" data-testid={`value-pyramid-${idx}-equation`}>1 semibreve = 2 minims = 4 crotchets = 8 quavers</p>
      {err && <p role="alert" className="text-sm text-[#A62B2B] mt-2" data-testid={`value-pyramid-${idx}-error`}>{err}</p>}
    </div>
  );
}

function MetreFeel({ s, idx }) {
  const [cur, setCur] = useState(-1);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const play = async () => {
    setErr("");
    setBusy(true);
    const beats = s.beats || 4, bars = s.bars || 2;
    let bi = 0;
    const timer = setInterval(() => {
      setCur(bi % beats);
      bi += 1;
      if (bi >= beats * bars) clearInterval(timer);
    }, 550);
    try {
      await playAudioSpec({ type: "metre", beats, bars, accents: s.accents });
    } catch (e) {
      console.error("[ChoirQuest audio] metre failed:", e);
      setErr("We couldn't play the sound. Check your volume and silent switch, then try again.");
    }
    clearInterval(timer);
    setCur(-1);
    setBusy(false);
  };
  return (
    <div className="rounded-2xl border-2 border-[#C69B3D]/50 bg-white p-5 space-y-4" data-testid={`metre-feel-${idx}`}>
      <p className="text-base text-[#1C242B] leading-relaxed">{s.instruction}</p>
      <div className="flex gap-3">
        {Array.from({ length: s.beats || 4 }).map((_, i) => (
          <div key={i} data-testid={`metre-feel-${idx}-beat-${i}`}
            className={`w-12 h-12 rounded-full flex items-center justify-center font-display font-bold text-lg transition-all ${cur === i ? "bg-[#C69B3D] text-[#0B192C] scale-110" : "bg-[#F3EFE0] text-[#566270]"}`}>{i + 1}</div>
        ))}
      </div>
      <button onClick={play} disabled={busy} data-testid={`metre-feel-${idx}-play`}
        className="inline-flex items-center gap-2 px-5 py-3.5 rounded-2xl bg-[#0B192C] text-[#FAF7EE] font-bold min-h-[52px] disabled:opacity-50">
        <Volume2 className="w-5 h-5" aria-hidden /> {busy ? "Playing…" : "▶ Play and count"}
      </button>
      {err && <p role="alert" className="text-sm text-[#A62B2B]" data-testid={`metre-feel-${idx}-error`}>{err}</p>}
    </div>
  );
}

function GroupingBeam({ s, idx }) {
  const n = s.notes || 6;
  const [selected, setSelected] = useState([]);
  const [groups, setGroups] = useState([]); // [{start, size}]
  const [result, setResult] = useState(null);
  const grouped = new Set();
  groups.forEach((g) => { for (let i = g.start; i < g.start + g.size; i++) grouped.add(i); });
  const toggle = (i) => {
    if (grouped.has(i) || result) return;
    setSelected((sel) => (sel.includes(i) ? sel.filter((x) => x !== i) : [...sel, i]));
  };
  const beam = () => {
    const sel = [...selected].sort((a, b) => a - b);
    if (sel.length < 2) return;
    if (sel.some((v, i) => i && v !== sel[i - 1] + 1)) {
      setResult({ ok: false, text: "A beam can only join quavers that sit side by side — tap a continuous run." });
      return;
    }
    setGroups((g) => [...g, { start: sel[0], size: sel.length }]);
    setSelected([]);
    setResult(null);
  };
  const check = () => {
    const covered = groups.reduce((a, g) => a + g.size, 0) === n &&
      [...groups].sort((a, b) => a.start - b.start).every((g, i, arr) => i === 0 || g.start === arr[i - 1].start + arr[i - 1].size) &&
      groups[0]?.start === 0;
    const right = JSON.stringify(groups.map((g) => g.size)) === JSON.stringify(s.expected);
    if (right && covered) {
      setResult({ ok: true, text: `Correct — ${s.metre} groups as ${s.expected.join(" + ")}. The beams now SHOW the metre.` });
    } else {
      setResult({ ok: false, text: `Not the ${s.metre} grouping. Six quavers in ${s.metre} need ${s.expected.join(" + ")} — three pairs would look like simple triple time. Reset and try again.` });
    }
  };
  const W = n * 46 + 20;
  return (
    <div className="rounded-2xl border-2 border-[#C69B3D]/50 bg-white p-5 space-y-4" data-testid={`grouping-${idx}`}>
      <p className="text-base text-[#1C242B] leading-relaxed">{s.instruction}</p>
      <svg width="100%" viewBox={`0 0 ${W} 78`} className="max-w-full" data-testid={`grouping-${idx}-stave`}>
        {[0, 1, 2, 3, 4].map((l) => <line key={l} x1="8" y1={46 + l * 6} x2={W - 8} y2={46 + l * 6} stroke="#0B192C" strokeWidth="1" />)}
        {groups.map((g) => (
          <rect key={`${g.start}-${g.size}`} x={20 + g.start * 46 + 9} y="10" width={(g.size - 1) * 46} height="6" fill="#7A1C2E" />
        ))}
        {Array.from({ length: n }).map((_, i) => {
          const x = 20 + i * 46;
          const isSel = selected.includes(i);
          return (
            <g key={i} onClick={() => toggle(i)} className="cursor-pointer" data-testid={`grouping-${idx}-note-${i}`}>
              <rect x={x - 12} y="0" width="40" height="76" fill={isSel ? "#F6EED8" : "transparent"} rx="6" />
              <line x1={x + 7} y1="46" x2={x + 7} y2="14" stroke={isSel ? "#C69B3D" : "#0B192C"} strokeWidth="2.5" />
              <ellipse cx={x} cy="46" rx="6.5" ry="5" transform={`rotate(-18 ${x} 46)`} fill={isSel ? "#C69B3D" : "#0B192C"} />
            </g>
          );
        })}
      </svg>
      <div className="flex gap-2 flex-wrap">
        <button onClick={beam} disabled={selected.length < 2} data-testid={`grouping-${idx}-beam-btn`}
          className="px-5 py-3 rounded-xl bg-[#0B192C] text-[#FAF7EE] font-bold text-sm min-h-[48px] disabled:opacity-40">Beam this group</button>
        <button onClick={check} disabled={!groups.length} data-testid={`grouping-${idx}-check`}
          className="px-5 py-3 rounded-xl bg-[#C69B3D] text-[#0B192C] font-bold text-sm min-h-[48px] disabled:opacity-40">Check my grouping</button>
        <button onClick={() => { setGroups([]); setSelected([]); setResult(null); }} data-testid={`grouping-${idx}-reset`}
          className="px-4 py-3 rounded-xl border-2 border-[#E5DEC9] text-[#566270] font-bold text-sm min-h-[48px]">Reset</button>
      </div>
      {result && <p className={`text-sm font-bold ${result.ok ? "text-[#2D6A4F]" : "text-[#A62B2B]"}`} data-testid={`grouping-${idx}-result`}>{result.text}</p>}
    </div>
  );
}

function CadenceTap({ s, idx }) {
  const [picked, setPicked] = useState(null);
  const done = picked !== null;
  const correct = picked === s.correct_index;
  return (
    <div className="rounded-2xl border-2 border-[#C69B3D]/50 bg-white p-5 space-y-4" data-testid={`cadence-tap-${idx}`}>
      <p className="text-base text-[#1C242B] leading-relaxed">{s.instruction}</p>
      {s.audio && <AudioButton audio={s.audio} idx={`cadence-${idx}`} label="Hear the half-verse sung" />}
      <div className="flex flex-wrap gap-2">
        {s.words.map((w, i) => (
          <button key={`${w}-${i}`} onClick={() => setPicked(i)} data-testid={`cadence-tap-${idx}-word-${i}`}
            className={`px-4 py-3 rounded-xl border-2 font-display text-lg min-h-[48px] transition-colors ${
              done && i === picked ? (correct ? "border-[#2D6A4F] bg-[#EBF6F0] text-[#2D6A4F]" : "border-[#A62B2B] bg-[#FBF0F2] text-[#A62B2B]")
                : "border-[#E5DEC9] bg-[#FAF7EE] text-[#0B192C] hover:border-[#C69B3D]"}`}>{w}</button>
        ))}
      </div>
      {done && <p className={`text-sm font-bold ${correct ? "text-[#2D6A4F]" : "text-[#A62B2B]"}`} data-testid={`cadence-tap-${idx}-feedback`}>{correct ? s.feedback_correct : s.feedback_wrong}</p>}
      {done && !correct && (
        <button onClick={() => setPicked(null)} data-testid={`cadence-tap-${idx}-retry`}
          className="px-4 py-2.5 rounded-xl border-2 border-[#0B192C] text-[#0B192C] font-bold text-sm min-h-[44px]">Try again</button>
      )}
    </div>
  );
}

function PsalmWalk({ s, idx }) {
  const [stage, setStage] = useState(0);
  const [playing, setPlaying] = useState("");
  const [err, setErr] = useState("");
  const play = async (key, spec) => {
    setErr("");
    setPlaying(key);
    try {
      await playAudioSpec(spec);
    } catch (e) {
      console.error("[ChoirQuest audio] psalm audio failed:", e);
      setErr("We couldn't play the sound. Check your volume and silent switch, then try again.");
    }
    setPlaying("");
  };
  const stages = ["The plain verse", "The pointed verse", "Beside the chant", "Hear it sung"];
  return (
    <div className="rounded-2xl border-2 border-[#7A1C2E]/40 bg-white p-5 space-y-4" data-testid={`psalm-walk-${idx}`}>
      <div className="flex gap-2 flex-wrap">
        {stages.map((t, i) => (
          <button key={t} onClick={() => setStage(i)} data-testid={`psalm-walk-${idx}-stage-${i}`}
            className={`px-3 py-2.5 rounded-xl text-xs font-bold min-h-[44px] transition-colors ${stage === i ? "bg-[#7A1C2E] text-[#FAF7EE]" : "bg-[#F3EFE0] text-[#566270]"}`}>{i + 1} · {t}</button>
        ))}
      </div>
      <p className="text-xs text-[#566270] italic">{s.psalm_title} — {s.psalm_source}</p>
      {stage === 0 && <p className="font-display text-lg leading-relaxed text-[#0B192C]" data-testid={`psalm-walk-${idx}-plain`}>{s.plain}</p>}
      {stage === 1 && (
        <>
          <figure className="rounded-xl overflow-hidden border-2 border-[#E5DEC9]" data-testid={`psalm-walk-${idx}-page`}>
            <img src={s.page_image} alt="The real pointed psalter page" className="w-full" loading="lazy" />
            <figcaption className="text-[11px] text-[#566270] italic p-2 bg-white">{s.page_credit}</figcaption>
          </figure>
          <p className="font-mono text-base leading-loose text-[#0B192C] bg-[#FAF7EE] rounded-xl p-4" data-testid={`psalm-walk-${idx}-pointed`}>{s.pointed}</p>
          <p className="text-sm text-[#566270]">Above is the REAL page from The Parish Psalter. The little marks ( ′ ) show where the words leave the reciting note. The syllables after each mark share the moving notes — say it aloud at speech speed and feel the stress.</p>
        </>
      )}
      {stage === 2 && (
        <>
          <figure className="rounded-xl overflow-hidden border-2 border-[#E5DEC9]" data-testid={`psalm-walk-${idx}-page-beside`}>
            <img src={s.detail_image || s.page_image} alt="The real psalter page with the chant above the pointed words" className="w-full" loading="lazy" />
            <figcaption className="text-[11px] text-[#566270] italic p-2 bg-white">{s.detail_credit || s.page_credit}</figcaption>
          </figure>
          <p className="font-mono text-sm leading-loose text-[#0B192C] bg-[#FAF7EE] rounded-xl p-3">{s.pointed}</p>
          <p className="text-sm text-[#566270]">{s.chant_work} — {s.chant_composer}. On the real page the chant sits right above the pointed words: the long breves are the reciting notes, the notes between them are the moving notes. Your treble line is the TOP line.</p>
          <button onClick={() => play("chant", s.chant_audio)} disabled={!!playing} data-testid={`psalm-walk-${idx}-chant-audio`}
            className="inline-flex items-center gap-2 px-5 py-3.5 rounded-2xl bg-[#0B192C] text-[#FAF7EE] font-bold min-h-[52px] disabled:opacity-50">
            <Volume2 className="w-5 h-5" aria-hidden /> {playing === "chant" ? "Playing…" : "▶ Hear the chant (all four parts)"}
          </button>
        </>
      )}
      {stage === 3 && (
        <div className="flex flex-col gap-2">
          <figure className="rounded-xl overflow-hidden border-2 border-[#E5DEC9]" data-testid={`psalm-walk-${idx}-page-sung`}>
            <img src={s.detail_image || s.page_image} alt="The real psalter page to follow while listening" className="w-full" loading="lazy" />
            <figcaption className="text-[11px] text-[#566270] italic p-2 bg-white">{s.detail_credit || s.page_credit}</figcaption>
          </figure>
          {[["chant", "Hear the chant alone (all four parts)", s.chant_audio],
            ["h1", "Hear the first half-verse sung", s.half_audio_1],
            ["h2", "Hear the second half-verse sung", s.half_audio_2],
            ["all", "Hear the whole verse sung (all four parts)", s.sung_audio]].map(([key, label, spec]) => (
            <button key={key} onClick={() => play(key, spec)} disabled={!!playing} data-testid={`psalm-walk-${idx}-${key}`}
              className="inline-flex items-center gap-2 px-5 py-3.5 rounded-2xl bg-[#7A1C2E] text-[#FAF7EE] font-bold min-h-[52px] disabled:opacity-50">
              <Volume2 className="w-5 h-5" aria-hidden /> {playing === key ? "Playing…" : `▶ ${label}`}
            </button>
          ))}
          <p className="text-sm text-[#566270]">Now sing it yourself: recite on the long note, move at the pointing marks ( ′ ).</p>
        </div>
      )}
      {err && <p role="alert" className="text-sm text-[#A62B2B]" data-testid={`psalm-walk-${idx}-error`}>{err}</p>}
    </div>
  );
}

const FOLLOW_BEATS = { w: 4, h: 2, q: 1, "8": 0.5, wr: 4, hr: 2, qr: 1, "8r": 0.5, qd: 1.5, hd: 3, wd: 6 };

function FollowAlong({ audio, containerRef, idx, recording = null, offsets = null }) {
  const [active, setActive] = useState(-1);
  const [busy, setBusy] = useState(false);
  const timersRef = useRef([]);
  const audioElRef = useRef(null);
  useEffect(() => () => {
    timersRef.current.forEach(clearTimeout);
    audioElRef.current?.pause();
  }, []);
  // Apply highlight after EVERY render (VexFlow may redraw the SVG at any time,
  // so never cache node references — always re-query the live DOM).
  useEffect(() => {
    const nodes = containerRef.current?.querySelectorAll(".vf-stavenote") || [];
    nodes.forEach((n, i) => { n.style.opacity = active === -1 || i === active ? "1" : "0.35"; });
  });
  const play = async () => {
    setBusy(true);
    setActive(0);
    if (recording && offsets?.length) {
      // Real recording: highlight from measured per-note offsets (ms). Timers run
      // independently of audio decode — if playback fails, the notes still light up.
      const el = new Audio(recording);
      audioElRef.current = el;
      const totalMs = offsets[offsets.length - 1] + 1400;
      offsets.forEach((ms, i) => timersRef.current.push(setTimeout(() => setActive(i), ms)));
      timersRef.current.push(setTimeout(() => setActive(-1), totalMs));
      timersRef.current.push(setTimeout(() => setBusy(false), totalMs + 100));
      el.onended = () => { timersRef.current.forEach(clearTimeout); setActive(-1); setBusy(false); };
      try { await el.play(); } catch (e) { console.error("[ChoirQuest audio] recording playback failed; highlights continue:", e); }
      return;
    }
    if (audio?.type !== "melody_vex") { setBusy(false); return; }
    let t = 150;
    (audio.notes || []).forEach((n, i) => {
      timersRef.current.push(setTimeout(() => setActive(i), t));
      t += (FOLLOW_BEATS[n.dur] ?? 1) * 550;
    });
    timersRef.current.push(setTimeout(() => setActive(-1), t));
    try {
      await playAudioSpec(audio);
    } catch (e) {
      console.error("[ChoirQuest audio] follow-along failed:", e);
    }
    timersRef.current.forEach(clearTimeout);
    setActive(-1);
    setBusy(false);
  };
  return (
    <button onClick={play} disabled={busy} data-testid={`follow-along-${idx}`}
      className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-2xl bg-[#C69B3D] text-[#0B192C] font-bold min-h-[52px] disabled:opacity-50 hover:bg-[#D8AB49] transition-colors">
      <Volume2 className="w-5 h-5" aria-hidden /> {busy ? "Follow the lit note — sing along!" : "▶ Sing along — notes light up as they play"}
    </button>
  );
}

function RealMusicSection({ s, idx }) {
  const containerRef = useRef(null);
  return (
    <div ref={containerRef} className="rounded-2xl border-2 border-[#C69B3D]/50 bg-white p-5 space-y-4" data-testid={`learn-realmusic-${idx}`}>
      <p className="text-sm font-bold text-[#0B192C]">
        Real music: <span className="text-[#7A1C2E]">{s.composer}</span> — {s.work} ({s.year})
      </p>
      {s.image && (
        <figure data-testid={`learn-realmusic-image-${idx}`}>
          <img src={s.image} alt={`Historical print of ${s.work}`} className="w-full rounded-xl border-2 border-[#E5DEC9]" loading="lazy" />
          <figcaption className="text-[11px] text-[#566270] italic mt-1.5">{s.image_credit}</figcaption>
        </figure>
      )}
      <StaveView notation={s.notation} width={Math.min(620, window.innerWidth - 96)} testid={`learn-realmusic-stave-${idx}`} />
      <div className="rounded-xl bg-[#0B192C] p-4">
        <AudioButton audio={s.audio} idx={`real-${idx}`} label="Hear the excerpt" />
      </div>
      {s.audio?.type === "melody_vex" && <FollowAlong audio={s.audio} containerRef={containerRef} idx={idx} />}
      {(s.recording_singalong || s.recording) && s.recording_offsets && s.audio?.type === "melody_vex" && (
        <div className="rounded-xl border-2 border-[#7A1C2E]/40 bg-[#FAF7EE] p-4" data-testid={`learn-realmusic-recsing-${idx}`}>
          <p className="text-xs font-bold text-[#7A1C2E] mb-2">🎧 Sing along with the REAL recording — notes light up:</p>
          <FollowAlong audio={s.audio} containerRef={containerRef} idx={`rec-${idx}`} recording={s.recording_singalong || s.recording} offsets={s.recording_offsets} />
          <p className="text-[11px] text-[#566270] italic mt-1.5">{s.recording_credit}</p>
        </div>
      )}
      {s.recording && (!s.recording_offsets || s.recording_singalong) && (
        <div className="rounded-xl border-2 border-[#7A1C2E]/40 bg-[#FAF7EE] p-4" data-testid={`learn-realmusic-recording-${idx}`}>
          <p className="text-xs font-bold text-[#7A1C2E] mb-2">🎧 Sing along with the real recording:</p>
          <audio controls preload="none" src={s.recording} className="w-full" data-testid={`learn-realmusic-audio-el-${idx}`} />
          <p className="text-[11px] text-[#566270] italic mt-1.5">{s.recording_credit}</p>
        </div>
      )}
      <p className="text-base text-[#1C242B] leading-relaxed">{s.notice}</p>
      <p className="text-[11px] text-[#566270] italic border-t border-[#E5DEC9] pt-2" data-testid={`learn-realmusic-provenance-${idx}`}>{s.provenance}</p>
    </div>
  );
}

function AudioWithScore({ s, idx }) {
  const ref = useRef(null);
  return (
    <div ref={ref} className="rounded-2xl border-2 border-[#C69B3D]/50 bg-white p-5 space-y-3" data-testid={`learn-audio-score-${idx}`}>
      <StaveView notation={s.notation} width={Math.min(620, window.innerWidth - 96)} testid={`learn-audio-score-stave-${idx}`} />
      <FollowAlong audio={s.audio} containerRef={ref} idx={idx} />
      {s.caption && <p className="text-sm text-[#566270] italic">{s.caption}</p>}
    </div>
  );
}

function SectionBody({ s, idx, refA4 }) {
  if (s.kind === "explain" || s.kind === "understand") {
    return <p className="text-base text-[#1C242B] leading-relaxed" data-testid={`learn-explain-${idx}`}>{s.body}</p>;
  }
  if (s.kind === "notation") {
    const notes = s.notation?.notes || [];
    const canTap = !s.notation?.parts && notes.length > 0 && notes.every((n) => !n.dur.endsWith("r") && (n.keys || []).length === 1);
    const tapNote = async (i) => { try { await playAudioSpec({ type: "melody_vex", notes: [notes[i]] }); } catch { /* surfaced by audio lib */ } };
    return (
      <div data-testid={`learn-notation-${idx}`}>
        <StaveView notation={s.notation} width={Math.min(620, window.innerWidth - 64)} testid={`learn-stave-${idx}`} onNoteClick={canTap ? tapNote : undefined} />
        {s.caption && <p className="text-sm text-[#566270] mt-2 italic">{s.caption}</p>}
        {canTap && <p className="text-xs font-bold text-[#8A6A1F] mt-2" data-testid={`learn-notation-tap-${idx}`}>Tap any note to hear it.</p>}
      </div>
    );
  }
  if (s.kind === "audio") {
    if (s.notation && s.audio?.type === "melody_vex") return <AudioWithScore s={s} idx={idx} />;
    return (
      <div className="rounded-2xl bg-[#0B192C] p-5">
        <AudioButton audio={s.audio} idx={idx} />
        {s.caption && <p className="text-sm text-[#FAF7EE]/80 mt-3">{s.caption}</p>}
      </div>
    );
  }
  if (s.kind === "audio_set") {
    return (
      <div className="space-y-3" data-testid={`learn-audioset-${idx}`}>
        {s.demos.map((d, j) => (
          <div key={d.label || j} className="rounded-2xl bg-[#0B192C] p-5">
            <AudioButton audio={d.audio} idx={`${idx}-${j}`} label={d.label} />
            {d.caption && <p className="text-sm text-[#FAF7EE]/80 mt-3">{d.caption}</p>}
          </div>
        ))}
      </div>
    );
  }
  if (s.kind === "image") {
    return (
      <figure className="rounded-xl overflow-hidden border-2 border-[#E5DEC9]" data-testid={`learn-image-${idx}`}>
        <img src={s.image} alt="Real historical print" className="w-full" loading="lazy" />
        {s.caption && <figcaption className="text-[11px] text-[#566270] italic p-2 bg-white">{s.caption}</figcaption>}
      </figure>
    );
  }
  if (s.kind === "activity") {
    if (s.activity === "tap_entry") return <TapEntry s={s} idx={idx} />;
    if (s.activity === "breath_points") return <BreathPoints s={s} idx={idx} />;
    if (s.activity === "conductor") return <ConductorDemo s={s} idx={idx} />;
    if (s.activity === "sing_check") return <SingCheck s={s} idx={idx} />;
    if (s.activity === "value_pyramid") return <ValuePyramid s={s} idx={idx} />;
    if (s.activity === "metre_feel") return <MetreFeel s={s} idx={idx} />;
    if (s.activity === "grouping") return <GroupingBeam s={s} idx={idx} />;
    if (s.activity === "cadence_tap") return <CadenceTap s={s} idx={idx} />;
    if (s.activity === "psalm_walk") return <PsalmWalk s={s} idx={idx} />;
    const NewActivity = { find_wrong_note: FindWrongNoteActivity, notation_pick: NotationPickActivity, pitch_match: PitchMatchActivity, sing_phrase: SingPhraseActivity, same_diff: SameDiffActivity, quick_pick: QuickPickActivity, term_match: MatchCardsActivity, match_my_pitch: MatchMyPitchActivity, sing_interval: SingIntervalActivity }[s.activity];
    if (NewActivity) return <PracticeShell s={s} idx={idx} Cmp={NewActivity} refA4={refA4} />;
    return null;
  }
  if (s.kind === "guided") {
    return (
      <div className="space-y-4" data-testid={`learn-guided-${idx}`}>
        <p className="text-base text-[#1C242B] leading-relaxed">{s.body}</p>
        {s.notation && <StaveView notation={s.notation} width={Math.min(620, window.innerWidth - 64)} testid={`learn-guided-stave-${idx}`} />}
        {s.audio && (
          <div className="rounded-2xl bg-[#0B192C] p-5">
            <AudioButton audio={s.audio} idx={`guided-${idx}`} label="Hear it again" />
          </div>
        )}
      </div>
    );
  }
  if (s.kind === "try") {
    return <CheckQuestion q={s.question} idx={idx} hint={s.hint} />;
  }
  if (s.kind === "check") {
    return <CheckQuestion q={s.question} idx={idx} />;
  }
  if (s.kind === "realmusic") {
    return <RealMusicSection s={s} idx={idx} />;
  }
  if (s.kind === "recap") {
    return (
      <div className="rounded-2xl bg-[#F6EED8] border border-[#C69B3D]/40 p-5" data-testid={`learn-points-${idx}`}>
        <p className="text-xs font-bold uppercase tracking-widest text-[#8a6a10] flex items-center gap-1.5">
          <ListChecks className="w-4 h-4" aria-hidden /> Remember
        </p>
        <ul className="mt-2 space-y-1.5">
          {s.points.map((pt, j) => (
            <li key={pt} className="text-sm font-semibold text-[#1C242B] flex gap-2">
              <span className="text-[#C69B3D]" aria-hidden>•</span>{pt}
            </li>
          ))}
        </ul>
      </div>
    );
  }
  return null;
}

export default function LearnPage() {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [step, setStep] = useState(1);
  const [refA4, setRefA4] = useState(DEFAULT_A4);

  useEffect(() => { installAudioUnlock(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    childApi.get("/child/tuning")
      .then((r) => setRefA4(r.data.concert_pitch || DEFAULT_A4))
      .catch(() => { /* default 440 */ });
  }, []);
  useEffect(() => {
    setStep(1);
    childApi.get(`/child/learn/${topicId}`)
      .then((r) => setData(r.data))
      .catch(() => setError("This lesson isn't available right now."));
  }, [topicId]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAF7EE] flex items-center justify-center px-5" data-testid="learn-error">
        <div className="text-center">
          <p className="text-[#566270]">{error}</p>
          <button onClick={() => navigate(-1)} className="mt-4 text-sm font-bold text-[#0B192C] underline min-h-[44px]">Go back</button>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen bg-[#FAF7EE] flex items-center justify-center"><Music className="w-8 h-8 text-[#C69B3D] animate-pulse" /></div>;
  }

  const total = data.sections[0]?.steps_total || 1;
  const stepSections = data.sections.filter((s) => s.step === step);
  const stepTitle = stepSections[0]?.step_title || "";
  const isLast = step >= total;
  const phase = stepSections.some((s) => ["activity", "try", "check"].includes(s.kind))
    ? "Practice"
    : stepSections.some((s) => ["realmusic", "recap"].includes(s.kind)) ? "Round up" : "Learn";

  return (
    <div className="min-h-screen bg-[#FAF7EE]" data-testid="learn-page">
      <header className="bg-[#0B192C] text-[#FAF7EE] px-5 py-5 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto flex items-center justify-between gap-3">
          <Link to="/play/home" className="flex items-center gap-2 shrink-0" data-testid="learn-home-link">
            <Music className="w-5 h-5 text-[#C69B3D]" aria-hidden />
            <span className="font-display font-bold hidden sm:inline">ChoirQuest</span>
          </Link>
          <div className="flex-1 max-w-xs">
            <div className="h-2 rounded-full bg-[#FAF7EE]/15 overflow-hidden" data-testid="learn-progress">
              <div className="h-full bg-[#C69B3D] transition-all" style={{ width: `${(step / total) * 100}%` }} />
            </div>
            <p className="text-[11px] text-[#FAF7EE]/70 mt-1 text-center">Step {step} of {total}</p>
          </div>
          <button onClick={() => navigate(-1)} data-testid="learn-back-btn"
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-[#C69B3D] text-[#0B192C] text-sm font-bold min-h-[48px] shrink-0">
            <ArrowLeft className="w-4 h-4" aria-hidden /> Exit
          </button>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-5 py-8 space-y-5">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-[#C69B3D]" data-testid="learn-phase">{phase} · {data.topic_name}</p>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0B192C] mt-1" data-testid="learn-step-title">{stepTitle}</h1>
        </div>
        {stepSections.map((s, i) => <SectionBody key={`${step}-${i}`} s={s} idx={i} refA4={refA4} />)}
        <div className="flex gap-3 pt-2">
          {step > 1 && (
            <button onClick={() => setStep(step - 1)} data-testid="learn-prev-btn"
              className="flex-1 px-6 py-4 rounded-2xl border-2 border-[#0B192C] text-[#0B192C] font-bold hover:bg-[#0B192C] hover:text-[#FAF7EE] transition-colors min-h-[52px]">
              Back
            </button>
          )}
          {!isLast ? (
            <button onClick={() => setStep(step + 1)} data-testid="learn-next-btn"
              className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-4 rounded-2xl bg-[#0B192C] text-[#FAF7EE] font-bold hover:bg-[#12253E] transition-colors min-h-[52px]">
              Next <ArrowRight className="w-4 h-4" aria-hidden />
            </button>
          ) : (
            <button onClick={() => navigate(-1)} data-testid="learn-done-btn"
              className="flex-1 px-6 py-4 rounded-2xl bg-[#C69B3D] text-[#0B192C] font-bold hover:bg-[#D8AB49] transition-colors min-h-[52px]">
              Ready for Challenge 1 — ask your choir leader to unlock it
            </button>
          )}
        </div>
      </main>
    </div>
  );
}
